import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useManualSubmit, useMinigameBackground } from '../hooks';
import { resolveMediaRefToUrl } from '../../../utils/mediaRef';
import { Icon } from '../../foundation/primitives';
import { FailOverlay, JudgementModal, MinigameHud, StartGate } from '../../foundation/semantic';
import { useMinigameTimer } from '../../foundation/hooks';

/**
 * AssetRef（{url?, assetId?, alt?} もしくは文字列）→ 表示用 URL。
 * 解決規則は hooks.js の useMinigameBackground と同じ「resolveMediaRefToUrl(値) ?? 生値」
 * （http(s) URL / assetId(UUID) を解決し、data: URI・相対パスは生値のまま通す）。
 * 未置換の {{asset:...}} プレースホルダは「画像なし」として扱い、壊れた img を出さない。
 */
function resolveAssetRefUrl(ref) {
  const raw = typeof ref === 'string' ? ref : ref?.url ?? ref?.assetId ?? '';
  if (typeof raw !== 'string') return null;
  const value = raw.trim();
  if (!value || value.startsWith('{{')) return null;
  return resolveMediaRefToUrl(value) ?? value;
}

/** スキーマ上 position.x/y は '25%' / '120px' 形式の文字列。数値が来た場合は % として扱う。 */
function toCssLength(value, fallback) {
  if (typeof value === 'number' && Number.isFinite(value)) return `${value}%`;
  if (typeof value === 'string' && value.trim()) return value.trim();
  return fallback;
}

// 非空文字列のみ採用（ホストは raw config を渡す＝Zod default 充填なし。型ゆらぎを自前で吸収する）
const pickText = (value, fallback) => (typeof value === 'string' && value.trim() !== '' ? value : fallback);

// プレイ条件（timeLimitMs / maxMistakes）の正規化: 正の有限数のみ有効。
// step スキーマの既定 0（=制限なし）と不正値は null に揃える（ゼロ config 回帰安全）
const toPlayLimit = (value) =>
  typeof value === 'number' && Number.isFinite(value) && value > 0 ? Math.floor(value) : null;

const MISS_TAP_BEHAVIORS = ['none', 'shake', 'penalize'];

const Template06Comparison = ({ hostSubmit, hostConfig, hostMeta, registryId = 'minigame_comparison_06', templateId = 6 }) => {
  // config 不在時の既定値（従来のデモ差分3点と同じ座標・ラベル）。画像は既定なし＝フォールバック表示。
  const defaultConfig = {
    differences: [
      { id: 'diff-vase', label: '花瓶', position: { x: '25%', y: '30%' } },
      { id: 'diff-clock', label: '時計', position: { x: '70%', y: '25%' } },
      { id: 'diff-book', label: '本', position: { x: '50%', y: '60%' } },
    ],
    missTapBehavior: 'shake',
  };
  // hostConfigの参照変化でリセットされないよう、JSON文字列化してメモ化キーにする
  const configKey = useMemo(() => JSON.stringify(hostConfig ?? {}), [hostConfig]);
  const resolvedConfig = useMemo(() => {
    const next = { ...defaultConfig, ...(hostConfig ?? {}) };
    if (!Array.isArray(next.differences) || next.differences.length === 0) {
      next.differences = defaultConfig.differences;
    }
    if (!MISS_TAP_BEHAVIORS.includes(next.missTapBehavior)) {
      next.missTapBehavior = defaultConfig.missTapBehavior;
    }
    return next;
  }, [configKey]);
  const backgroundStyle = useMinigameBackground(hostConfig);
  const differences = resolvedConfig.differences;
  const currentImageUrl = resolveAssetRefUrl(resolvedConfig.currentImage);
  const pastImageUrl = resolveAssetRefUrl(resolvedConfig.pastImage);

  // R1 体験文法: StartGate / MinigameHud の見出しと説明（config 優先・既定は常体の一行）
  const title = pickText(resolvedConfig.title, '違いを探せ');
  const instruction = pickText(resolvedConfig.instructionText, '二つの記録を見比べ、違いを指摘せよ');
  const failMessageConfig =
    typeof resolvedConfig.failMessage === 'object' && resolvedConfig.failMessage !== null ? resolvedConfig.failMessage : {};

  const [foundIds, setFoundIds] = useState(() => new Set());
  const [gameState, setGameState] = useState('playing');
  const [missShake, setMissShake] = useState(false);
  const [missCount, setMissCount] = useState(0);
  // R1 体験文法: 開始ゲート。step が showTutorial===false を明示した時だけ最初から開けておく
  const [gateOpen, setGateOpen] = useState(() => hostMeta?.ui?.showTutorial !== false);
  const [retryCount, setRetryCount] = useState(0);
  const [failReason, setFailReason] = useState(null); // 'timeout' | 'mistakes'
  const { submit, resetSubmission } = useManualSubmit(registryId, templateId, hostSubmit);
  const initializedRef = useRef(false);

  // プレイ条件: config 優先 → step(hostMeta)。0 / 不正値は「制限なし」（従来挙動のまま）
  const timeLimitMs = toPlayLimit(hostConfig?.timeLimitMs ?? hostMeta?.timeLimitMs);
  const mistakeLimit = toPlayLimit(hostConfig?.maxMistakes ?? hostMeta?.maxMistakes);

  const failGame = (reason) => {
    setFailReason(reason);
    // success との競合は「先に確定した方が勝つ」（playing の時だけ fail に落とす）
    setGameState((prev) => (prev === 'playing' ? 'fail' : prev));
  };

  // 実動タイマー: ゲート表示中は停止。満了で fail（limit 未設定なら remainingMs=null で無効）
  const { remainingMs, reset: resetTimer } = useMinigameTimer({
    limitMs: timeLimitMs,
    running: !gateOpen && gameState === 'playing',
    onExpire: () => failGame('timeout'),
  });

  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;
    setFoundIds(new Set());
    setGameState('playing');
    setMissShake(false);
    setMissCount(0);
    resetSubmission();
  }, [resetSubmission]);

  const handleFind = (id) => {
    if (gameState !== 'playing') return;
    if (foundIds.has(id)) return;
    const next = new Set(foundIds);
    next.add(id);
    setFoundIds(next);
    if (next.size >= differences.length) {
      // 遅延中にタイマー満了で fail し得るため、playing のままの時だけ success に確定する
      setTimeout(() => setGameState((prev) => (prev === 'playing' ? 'success' : prev)), 500);
    }
  };

  // 違いマーカー以外（現在フレームの画像部分）をタップしたときの反応。
  // R1: ミスタップは missTapBehavior（見た目の反応）と独立に常時計上し、上限判定と penalize 表示で共用する。
  // R0 の反応自体は不変（none=無反応 / shake・penalize=枠 shake、penalize はミス回数の表示つき）。
  const handleMissTap = () => {
    if (gameState !== 'playing') return;
    const nextMissCount = missCount + 1;
    setMissCount(nextMissCount);
    if (mistakeLimit != null && nextMissCount >= mistakeLimit) {
      failGame('mistakes');
      return;
    }
    if (resolvedConfig.missTapBehavior === 'none') return;
    setMissShake(true);
  };

  // FailOverlay「やり直す」: 内部リセット + retryCount 加算（submit はまだ起きていないので resetSubmission 不要）
  const handleRetry = () => {
    setFoundIds(new Set());
    setMissShake(false);
    setMissCount(0);
    setFailReason(null);
    setGameState('playing');
    setRetryCount((count) => count + 1);
    resetTimer();
  };

  const foundCount = foundIds.size;
  const totalCount = differences.length;

  // HUD チップ: showTimer===false / showMistakeCount===false は表示のみ抑止（計時・上限判定は生きる）。
  // 進捗 n/total は HUD に統合（フッターの数字表示は撤去し、進捗バーだけ本体に残す＝二重表示を避ける）
  const hudTimer =
    timeLimitMs != null && remainingMs != null && hostConfig?.showTimer !== false
      ? { remainingMs, totalMs: timeLimitMs }
      : null;
  const hudMistakes =
    mistakeLimit != null && hostConfig?.showMistakeCount !== false
      ? { current: missCount, max: mistakeLimit }
      : null;

  // 画像未設定フレームの控えめなフォールバック注記（開発用語を出さない）。
  const renderFallbackNote = () => (
    <div className="t06-frame-fallback absolute inset-0 flex items-center justify-center">
      <p className="text-sm">記録が読み込まれていない</p>
    </div>
  );

  return (
    <div className="t06-root relative w-full h-full overflow-hidden" style={backgroundStyle ?? undefined}>
      <div className="t06-bg-overlay absolute inset-0" />

      {/* R1 体験文法: 独自絵文字ヘッダ＋飾りメニューを撤去し、共通 HUD に置換。? は開始ゲート再表示 */}
      <MinigameHud
        title={title}
        icon="Eye"
        timer={hudTimer}
        progress={{ current: foundCount, total: totalCount }}
        mistakes={hudMistakes}
        onHelp={() => setGateOpen(true)}
      />

      <div className="absolute top-14 left-0 right-0 h-[38%] p-2">
        <div
          className="t06-frame-current relative w-full h-full border-2 rounded-xl overflow-hidden"
          style={missShake ? { animation: 't06-miss-shake 0.3s ease-in-out' } : undefined}
          onAnimationEnd={() => setMissShake(false)}
          onClick={handleMissTap}
        >
          {currentImageUrl ? (
            <img
              src={currentImageUrl}
              alt={resolvedConfig.currentImage?.alt ?? '現在の記録'}
              className="absolute inset-0 w-full h-full object-cover select-none pointer-events-none"
              draggable={false}
            />
          ) : (
            renderFallbackNote()
          )}
          <div className="t06-frame-label-current absolute top-2 left-2 text-xs px-2 py-1 rounded">現在</div>
          {differences.map((diff) => (
            <button
              key={diff.id}
              className={`absolute w-10 h-10 -translate-x-1/2 -translate-y-1/2 rounded-full transition-all
                ${foundIds.has(diff.id) ? 't06-marker--found' : 't06-marker--idle'}`}
              style={{
                left: toCssLength(diff.position?.x, '50%'),
                top: toCssLength(diff.position?.y, '50%'),
                ...(diff.size?.width ? { width: diff.size.width } : {}),
                ...(diff.size?.height ? { height: diff.size.height } : {}),
              }}
              onClick={(e) => {
                e.stopPropagation();
                handleFind(diff.id);
              }}
            >
              {foundIds.has(diff.id) && <Icon name="CircleCheck" size={24} className="t06-marker-icon mx-auto" />}
            </button>
          ))}
        </div>
      </div>

      <div className="absolute top-[52%] left-0 right-0 h-[30%] p-2">
        <div className="t06-frame-past relative w-full h-full border-2 rounded-xl overflow-hidden opacity-80">
          {pastImageUrl ? (
            <img
              src={pastImageUrl}
              alt={resolvedConfig.pastImage?.alt ?? '過去の記録'}
              className="absolute inset-0 w-full h-full object-cover select-none pointer-events-none"
              draggable={false}
            />
          ) : (
            renderFallbackNote()
          )}
          <div className="t06-frame-label-past absolute top-2 left-2 text-xs px-2 py-1 rounded">過去の写真</div>
        </div>
      </div>

      <div className="t06-footer absolute bottom-0 left-0 right-0 z-30 pt-4 pb-6 px-4">
        <div className="t06-footer-panel rounded-xl p-3">
          <p className="t06-footer-instruction text-sm mb-2">現在と過去の違いを見つけろ</p>
          <div className="flex items-center gap-3">
            <div className="t06-progress-track flex-1 h-2 rounded-full overflow-hidden">
              <div
                className="t06-progress-fill h-full transition-all"
                style={{ width: `${(foundCount / totalCount) * 100}%` }}
              />
            </div>
            {/* penalize のミス回数表示は、HUD「ミス n/max」が出る（=上限設定あり）場合は二重になるため出さない */}
            {resolvedConfig.missTapBehavior === 'penalize' && mistakeLimit == null && (
              <span className="t06-miss-count text-sm font-mono">ミス: {missCount}</span>
            )}
          </div>
        </div>
      </div>

      {/* R1 体験文法: 開始ゲート（表示中は実動タイマーも停止する） */}
      {gateOpen && (
        <StartGate
          title={title}
          instruction={instruction}
          icon="Eye"
          onStart={() => setGateOpen(false)}
        />
      )}

      {/* R1 体験文法: fail（時間切れ / ミス上限到達）の明示フィードバック。
          やり直す = 内部リセット / 未整理のまま進む = submit('fail')。文言は config.failMessage 優先 */}
      {gameState === 'fail' && (
        <FailOverlay
          title={pickText(failMessageConfig.title, undefined)}
          subtitle={pickText(failMessageConfig.subtitle, failReason === 'timeout' ? '時間切れ' : 'ミスが上限に達した')}
          allowRetry={failMessageConfig.allowRetry !== false}
          onRetry={handleRetry}
          onProceed={() => submit('fail', { retryCount })}
        />
      )}

      {/* Foundation: 成功モーダル（中央バッジ + タイトル + 説明 + 次へ進む を JudgementModal に集約）。
          文言は config.successMessage（BaseConfig）を優先し、未指定なら従来文言。 */}
      <JudgementModal
        open={gameState === 'success'}
        type="success"
        title={resolvedConfig.successMessage?.title ?? '照合完了！'}
        description={resolvedConfig.successMessage?.subtitle ?? 'すべての違いを発見'}
        icon={<Icon name="Eye" size={36} />}
        buttonLabel="次へ進む"
        onSubmit={() => submit('success', { retryCount })}
      />
    </div>
  );
};

export default Template06Comparison;
